import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { contractorApi } from '../../lib/contractorApi';
import { useContractorAuthStore } from '../../lib/contractorAuth';

interface ContractorProfile {
  id: string;
  business_name: string;
  owner_name: string;
  tier: string;
  review_score: number;
  review_count: number;
  status: string;
  profile_completeness_score: number | null;
  bio: string | null;
  license_number: string | null;
  insurance_cert_url: string | null;
  response_badge: string | null;
  phone: string | null;
  website_url: string | null;
}

interface AnalyticsData {
  period_days: number;
  leads: { total: number; new: number; accepted: number; passed: number };
  jobs: { total: number; completed_count: number; total_revenue: number };
  credits: { balance: number };
  reviews: { total: number; avg_overall: number | null };
}

interface Lead {
  id: string;
  lead_ref: string;
  trade: string;
  urgency: string;
  status: string;
  credit_cost: number;
  created_at: string;
  expires_at: string;
}

interface LeadsResponse { data: Lead[] }

interface Review {
  id: string;
  overall_rating: number;
  reviewer_display_name: string;
  review_text: string | null;
  response_text: string | null;
  has_response: boolean;
  response_deadline: string | null;
  job_title: string | null;
  created_at: string;
}

interface ReviewsResponse { data: Review[] }

function ReviewCard({ review }: { review: Review }) {
  const [draft, setDraft] = useState<string | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);

  async function fetchDraft() {
    setDrafting(true);
    setDraftError(null);
    try {
      const d = await contractorApi.post<{ data: { draft: string } }>(`/contractor/reviews/${review.id}/draft-response`, {});
      setDraft(d.data.draft);
    } catch {
      setDraftError('Network error. Try again.');
    } finally {
      setDrafting(false);
    }
  }

  return (
    <div style={{ padding: '12px 16px', borderBottom: '1px solid #f9fafb' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
          {review.reviewer_display_name}
        </span>
        <span style={{ color: '#f59e0b', fontSize: 13 }}>
          {'â˜…'.repeat(review.overall_rating)}{'â˜†'.repeat(5 - review.overall_rating)}
        </span>
      </div>
      {review.review_text && (
        <p style={{ margin: '0 0 6px', fontSize: 13, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {review.review_text}
        </p>
      )}
      {!review.has_response && review.review_text && (
        <button
          onClick={fetchDraft}
          disabled={drafting}
          style={{
            background: 'none', border: '1px solid #d1d5db', borderRadius: 8,
            padding: '4px 10px', fontSize: 11, color: '#374151', cursor: 'pointer',
            fontWeight: 500, marginBottom: draft ? 8 : 0,
          }}
        >
          {drafting ? 'Draftingâ€¦' : 'âœ¨ Draft a response'}
        </button>
      )}
      {draftError && <p style={{ margin: '4px 0 0', fontSize: 11, color: '#dc2626' }}>{draftError}</p>}
      {draft && (
        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', marginTop: 6 }}>
          <p style={{ margin: '0 0 6px', fontSize: 11, color: '#8b5cf6', fontWeight: 600 }}>AI draft â€” review before posting</p>
          <p style={{ margin: 0, fontSize: 13, color: '#374151' }}>{draft}</p>
          <button
            onClick={() => window.location.href = '/app/contractor/reviews'}
            style={{
              marginTop: 8, background: '#9333EA', color: '#fff', border: 'none',
              borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Post response
          </button>
        </div>
      )}
      {!review.has_response && review.response_deadline && (
        <p style={{ margin: '4px 0 0', fontSize: 11, color: '#f59e0b', fontWeight: 500 }}>
          Respond by {new Date(review.response_deadline).toLocaleDateString()}
        </p>
      )}
    </div>
  );
}

const TRADE_LABELS: Record<string, string> = {
  hvac: 'HVAC', plumbing: 'Plumbing', electrical: 'Electrical',
  roofing: 'Roofing', general: 'General Contractor',
  pest_control: 'Pest Control', landscaping: 'Landscaping',
  painting: 'Painting', appliance_repair: 'Appliance Repair', flooring: 'Flooring',
};

const URGENCY_COLOR: Record<string, string> = {
  critical: '#dc2626', high: '#f59e0b', medium: '#3b82f6', low: '#22c55e',
};

function ProfileCompletenessCard({ profile, onNavigate }: { profile: ContractorProfile; onNavigate: (path: string) => void }) {
  const score = profile.profile_completeness_score ?? 0;
  const steps: { done: boolean; label: string; path: string }[] = [
    { done: !!profile.bio && profile.bio.length > 20, label: 'Add a business bio (20+ chars)', path: '/app/contractor/profile' },
    { done: !!profile.license_number, label: 'Add your license number', path: '/app/contractor/profile' },
    { done: !!profile.insurance_cert_url, label: 'Upload certificate of insurance', path: '/app/contractor/profile' },
    { done: !!profile.phone, label: 'Add a phone number', path: '/app/contractor/profile' },
    { done: !!profile.website_url, label: 'Add your website', path: '/app/contractor/profile' },
    { done: !!profile.response_badge, label: 'Win a response badge (respond quickly!)', path: '/app/contractor/jobs' },
  ];
  const pending = steps.filter(s => !s.done);
  if (pending.length === 0) return null;

  const circumference = 2 * Math.PI * 20;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 75 ? '#22c55e' : score >= 50 ? '#f59e0b' : '#3b82f6';

  return (
    <div style={{ background: '#fff', borderRadius: 16, padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
        <svg width="48" height="48" viewBox="0 0 48 48">
          <circle cx="24" cy="24" r="20" fill="none" stroke="#f3f4f6" strokeWidth="4" />
          <circle cx="24" cy="24" r="20" fill="none" stroke={color} strokeWidth="4"
            strokeDasharray={circumference} strokeDashoffset={offset}
            strokeLinecap="round" transform="rotate(-90 24 24)" />
          <text x="24" y="28" textAnchor="middle" fontSize="11" fontWeight="700" fill={color}>{score}%</text>
        </svg>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#111827' }}>Profile completeness</h3>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>
            {pending.length} item{pending.length === 1 ? '' : 's'} left to improve visibility
          </p>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {pending.slice(0, 3).map(s => (
          <button
            key={s.label}
            type="button"
            onClick={() => onNavigate(s.path)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, background: '#f9fafb',
              border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px',
              cursor: 'pointer', textAlign: 'left', width: '100%',
            }}
          >
            <span style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #d1d5db', flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: '#374151' }}>{s.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function ContractorDashboard() {
  const navigate     = useNavigate();
  const contractor   = useContractorAuthStore((s) => s.contractor);
  const [billingLoading, setBillingLoading] = useState(false);

  const { data: me } = useQuery({
    queryKey: ['contractor-me'],
    queryFn: () => contractorApi.get<{ data: ContractorProfile }>('/contractor/me'),
  });

  const { data: analytics } = useQuery({
    queryKey: ['contractor-analytics'],
    queryFn: () => contractorApi.get<{ data: AnalyticsData }>('/contractor/analytics?days=30'),
  });

  const { data: leadsRes } = useQuery({
    queryKey: ['contractor-leads-new'],
    queryFn: () => contractorApi.get<LeadsResponse>('/contractor/leads?status=new&limit=5'),
  });

  const { data: reviewsRes } = useQuery({
    queryKey: ['contractor-reviews-recent'],
    queryFn: () => contractorApi.get<ReviewsResponse>('/contractor/reviews?limit=3'),
  });

  const profile      = me?.data;
  const stats        = analytics?.data;
  const newLeads     = leadsRes?.data ?? [];
  const recentReviews = reviewsRes?.data ?? [];

  async function openBillingPortal() {
    setBillingLoading(true);
    try {
      const res = await contractorApi.post<{ url: string }>('/contractor/billing/portal');
      window.location.href = res.url;
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to open billing portal.';
      alert(msg);
    } finally {
      setBillingLoading(false);
    }
  }

  const isStarter = contractor?.tier === 'self_service';

  return (
    <>
      <style>{`
        @media (min-width: 768px) {
          .cdb-layout { padding-left: 256px !important; }
          .cdb-grid { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 20px !important; }
          .cdb-full { grid-column: 1 / -1 !important; }
          .cdb-stats { grid-template-columns: repeat(4, 1fr) !important; }
        }
      `}</style>

      <div className="cdb-layout" style={{ minHeight: '100dvh', background: '#f9fafb' }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #9333EA 0%, #7E22CE 100%)',
          padding: '48px 20px 28px',
        }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <p style={{ margin: '0 0 4px', color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
                Contractor Portal
              </p>
              <h1 style={{ margin: 0, color: '#fff', fontSize: 22, fontWeight: 700 }}>
                {profile?.business_name ?? contractor?.business_name ?? 'Your Business'}
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                <span style={{
                  background: profile?.status === 'verified' ? '#22c55e' : '#f59e0b',
                  color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 7px',
                  borderRadius: 99, textTransform: 'uppercase', letterSpacing: '0.05em',
                }}>
                  {profile?.status ?? 'pending'}
                </span>
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                  {profile?.tier?.replace('_', ' ') ?? 'starter'} plan
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
              <div style={{ textAlign: 'right' }}>
                <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>Credits</p>
                <p style={{ margin: 0, color: '#fff', fontSize: 26, fontWeight: 800 }}>
                  {stats?.credits.balance ?? 'â€”'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void openBillingPortal()}
                disabled={billingLoading}
                style={{
                  padding: '8px 16px', background: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.3)', color: '#fff',
                  borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: billingLoading ? 'not-allowed' : 'pointer',
                  backdropFilter: 'blur(4px)',
                }}
              >
                {billingLoading ? 'Loadingâ€¦' : 'Manage billing'}
              </button>
            </div>
          </div>
        </div>

        <div style={{ padding: '16px 16px 100px', maxWidth: 1100, margin: '0 auto' }}>
          {/* Upgrade banner for starter tier */}
          {isStarter && (
            <div style={{
              background: 'linear-gradient(135deg, #7E22CE, #9333EA)', borderRadius: 14,
              padding: '16px 20px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
            }}>
              <div>
                <p style={{ margin: 0, color: '#fff', fontWeight: 700, fontSize: 14 }}>Upgrade to Pro</p>
                <p style={{ margin: '2px 0 0', color: 'rgba(255,255,255,0.75)', fontSize: 13 }}>
                  Get verified badge, exclusive leads, and priority search ranking â€” $49/mo.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/contractor/signup?plan=pro')}
                style={{
                  padding: '8px 18px', background: '#fff', color: '#9333EA',
                  borderRadius: 10, fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer', flexShrink: 0,
                }}
              >
                Upgrade
              </button>
            </div>
          )}

          {/* Stats grid */}
          <div className="cdb-stats" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
            {[
              { label: 'New leads', value: stats?.leads.new ?? 'â€”', color: '#3b82f6' },
              { label: 'Completed jobs', value: stats?.jobs.completed_count ?? 'â€”', color: '#22c55e' },
              { label: 'Avg rating', value: stats?.reviews.avg_overall != null ? stats.reviews.avg_overall.toFixed(1) + 'â˜…' : 'â€”', color: '#f59e0b' },
              { label: 'Revenue (30d)', value: stats?.jobs.total_revenue != null ? `$${stats.jobs.total_revenue.toLocaleString()}` : 'â€”', color: '#8b5cf6' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{
                background: '#fff', borderRadius: 14, padding: '14px 16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
              }}>
                <p style={{ margin: '0 0 4px', fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {label}
                </p>
                <p style={{ margin: 0, fontSize: 22, fontWeight: 800, color }}>{value}</p>
              </div>
            ))}
          </div>

          {/* Profile completeness card */}
          {profile && (profile.profile_completeness_score ?? 0) < 100 && (
            <ProfileCompletenessCard profile={profile} onNavigate={navigate} />
          )}

          <div className="cdb-grid" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* New leads preview */}
            <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
              <div style={{ padding: '14px 16px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f3f4f6' }}>
                <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#111827' }}>New leads</h2>
                <button
                  type="button"
                  onClick={() => navigate('/app/contractor/leads')}
                  style={{ background: 'none', border: 'none', color: '#9333EA', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 }}
                >
                  View all
                </button>
              </div>
              {newLeads.length === 0 ? (
                <p style={{ margin: 0, padding: '20px 16px', fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>
                  No new leads right now.
                </p>
              ) : (
                newLeads.map(lead => (
                  <div
                    key={lead.id}
                    style={{ padding: '12px 16px', borderBottom: '1px solid #f9fafb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                    onClick={() => navigate('/app/contractor/leads')}
                  >
                    <div>
                      <p style={{ margin: 0, fontWeight: 600, fontSize: 13, color: '#111827' }}>
                        {TRADE_LABELS[lead.trade] ?? lead.trade}
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>
                        {lead.credit_cost} credit{lead.credit_cost !== 1 ? 's' : ''} Â· {lead.lead_ref}
                      </p>
                    </div>
                    <span style={{
                      padding: '3px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600,
                      background: `${URGENCY_COLOR[lead.urgency] ?? '#6b7280'}18`,
                      color: URGENCY_COLOR[lead.urgency] ?? '#6b7280',
                      textTransform: 'capitalize',
                    }}>
                      {lead.urgency}
                    </span>
                  </div>
                ))
              )}
            </div>

            {/* Recent reviews */}
            <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
              <div style={{ padding: '14px 16px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f3f4f6' }}>
                <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#111827' }}>Recent reviews</h2>
                <button
                  type="button"
                  onClick={() => navigate('/app/contractor/analytics')}
                  style={{ background: 'none', border: 'none', color: '#9333EA', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 }}
                >
                  Analytics
                </button>
              </div>
              {recentReviews.length === 0 ? (
                <p style={{ margin: 0, padding: '20px 16px', fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>
                  No reviews yet.
                </p>
              ) : (
                recentReviews.map(review => (
                  <ReviewCard key={review.id} review={review} />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
